import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Flame, Check, X, ChevronLeft, Sparkles } from "lucide-react";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";

export default function Pricing() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [trialDays, setTrialDays] = useState(7);
  const [loadingKey, setLoadingKey] = useState(null);

  useEffect(() => {
    api.get("/plans").then((r) => { setPlans(r.data.plans || []); setTrialDays(r.data.trial_days || 7); }).catch(() => {});
  }, []);

  const choose = async (plan) => {
    if (!user) { nav("/auth?mode=register"); return; }
    if (user.role === "coach") { toast.info("Les comptes coach n'ont pas besoin d'abonnement."); return; }
    setLoadingKey(plan.lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: plan.lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
      setLoadingKey(null);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="flex items-center justify-between px-5 md:px-10 py-5 border-b border-border">
        <button className="flex items-center gap-2" onClick={() => nav(user ? (user.role === "coach" ? "/coach" : "/app") : "/")} data-testid="pricing-home-btn">
          <Flame className="text-primary" size={24} strokeWidth={2.5} />
          <span className="font-display text-2xl">ROX<span className="text-primary">LAB</span></span>
        </button>
        {user ? (
          <Button variant="ghost" onClick={() => nav(user.role === "coach" ? "/coach" : "/app")} className="text-sm"><ChevronLeft size={16} /> Retour</Button>
        ) : (
          <Button variant="ghost" onClick={() => nav("/auth")} className="text-sm">Connexion</Button>
        )}
      </header>

      <div className="max-w-4xl mx-auto px-5 py-12 md:py-16">
        <div className="text-center mb-10">
          <Badge className="bg-primary/15 text-primary border-0 mb-4"><Sparkles size={13} className="mr-1" /> {trialDays} jours d'essai gratuit</Badge>
          <h1 className="font-display text-4xl md:text-5xl">Choisis ta formule</h1>
          <p className="text-muted-foreground mt-3 max-w-lg mx-auto">Commence par {trialDays} jours gratuits. Passe à l'abonnement quand tu es prêt à performer sur le long terme.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          {plans.map((p, i) => (
            <motion.div key={p.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
              data-testid={`plan-card-${p.id}`}
              className={`relative rounded-lg p-6 border ${p.highlight ? "border-primary bg-card" : "border-border bg-card"}`}>
              {p.highlight && <Badge className="absolute -top-3 left-6 bg-primary text-white border-0">Le plus complet</Badge>}
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{p.tagline}</p>
              <h3 className="font-display text-2xl mt-1">{p.name}</h3>
              <div className="mt-3 mb-5">
                <span className="font-display text-4xl">{p.price}</span>
                <span className="text-muted-foreground text-sm">{p.period}</span>
              </div>
              <ul className="space-y-2.5 mb-6">
                {p.features.map((f, fi) => (
                  <li key={fi} className="flex items-start gap-2 text-sm"><Check size={17} className="text-primary mt-0.5 shrink-0" /> {f}</li>
                ))}
                {(p.excluded || []).map((f, fi) => (
                  <li key={fi} className="flex items-start gap-2 text-sm text-muted-foreground"><X size={17} className="mt-0.5 shrink-0" /> {f}</li>
                ))}
              </ul>
              <Button data-testid={`plan-cta-${p.id}`} onClick={() => choose(p)} disabled={loadingKey === p.lookup_key}
                className={`w-full h-13 py-3 rounded-md font-semibold active:scale-95 transition-transform ${p.highlight ? "bg-primary hover:bg-primary/90 text-white" : "bg-secondary hover:bg-secondary/80 text-foreground border border-border"}`}>
                {loadingKey === p.lookup_key ? "Redirection…" : user ? "S'abonner" : `Démarrer l'essai gratuit`}
              </Button>
            </motion.div>
          ))}
        </div>
        <p className="text-center text-xs text-muted-foreground mt-8">Paiement sécurisé par Stripe · Carte de test : 4242 4242 4242 4242</p>
      </div>
    </div>
  );
}
